import { useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { Navigation } from 'components'
import colors from 'utils/styling/colors'
import typeArea from 'styles/typeArea'
import { isSuperAdmin } from '@/hooks/utility/useIsSuperAdmin'
import { userAtom } from '@/atoms/_globalAtoms'
import { transparentPrintBackgroundPageEntry } from './transparentPrintBackgroundPageEntry'
import { filterPrintProductRows } from './printProductRows'
import { useTransparentPrintBackgroundProducts } from './useTransparentPrintBackgroundProducts'

const TID = 'transparent-print'

/**
 * The admin page where a super admin chooses the MDL products whose final
 * PDFs print with no page background, so a UV press printing on a dark
 * product (an urn) lays no white ink where the design leaves the page empty.
 * Proofs are not affected. M3 products are set in the M3 admin's product
 * editor, so the page lists MDL products only. The page is internal, so its copy is plain English.
 * Anyone but a super admin, whom the route it calls also requires, is sent to
 * the dashboard.
 */
export const TransparentPrintBackgroundPage = () => {
  const user = useRecoilValue(userAtom)
  const allowed = user !== null && isSuperAdmin(user)
  const { rows, error, toggle, pendingIds } =
    useTransparentPrintBackgroundProducts(allowed)
  const [query, setQuery] = useState('')
  const shown = useMemo(
    () => (rows === null ? [] : filterPrintProductRows(rows, query)),
    [rows, query],
  )

  // The signed-in user arrives after the first render on a direct load, so
  // no user yet means still loading, not "not a super admin".
  if (user === null) return null
  if (!allowed) return <Redirect to="/dashboard" />

  return (
    <>
      <Navigation type="admin" />
      <Page data-testid={`${TID}-page`}>
        <Title>{transparentPrintBackgroundPageEntry.title}</Title>
        <Subtitle>
          Final PDFs of an MDL product turned on here paint no white page behind
          the design, so a UV press printing on a dark product such as an urn
          lays no white ink where the design leaves the page empty. White the
          design itself holds still prints. Proofs are not affected. M3 products
          are set in the M3 admin&apos;s product editor.
        </Subtitle>
        {error !== null && (
          <ErrorText role="alert" data-testid={`${TID}-error`}>
            {error}
          </ErrorText>
        )}
        {rows === null ? (
          error === null && <p data-testid={`${TID}-loading`}>Loading…</p>
        ) : (
          <>
            <SearchInput
              type="search"
              value={query}
              placeholder="Product name"
              aria-label="Product name"
              onChange={(event) => setQuery(event.target.value)}
              data-testid={`${TID}-search-input`}
            />
            {shown.length === 0 && (
              <p data-testid={`${TID}-empty`}>No product matches.</p>
            )}
            <List>
              {shown.map((row) => (
                <ListItem key={row.id} data-testid={`${TID}-row-${row.id}`}>
                  <Label>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={row.enabled}
                      disabled={pendingIds.has(row.id)}
                      onChange={() => void toggle(row)}
                      data-testid={`${TID}-toggle-${row.id}`}
                    />
                    <span>{row.name}</span>
                  </Label>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Page>
    </>
  )
}

/**
 * The page takes the theme's text colour from here: the app's global styles
 * set a themed background but no text colour, so without it the text keeps
 * the browser's black and disappears on the dark theme.
 */
const Page = styled.div`
  padding: 32px;
  max-width: 720px;
  color: ${colors('gray1')};
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
`

const Subtitle = styled.p`
  margin: 0 0 24px 0;
  color: ${colors('gray2')};
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const ListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid ${colors('gray4')};
`

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

/** The search field, in the app's shared field style (as the priority Input). */
const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  height: 42px;
  margin-bottom: 8px;

  ${typeArea}
`

const ErrorText = styled.p`
  color: ${colors('error')};
`
